import { ComponentType, Overlay } from '@angular/cdk/overlay';
import { ComponentPortal, TemplatePortal } from '@angular/cdk/portal';
import { Injectable, Injector, TemplateRef, inject } from '@angular/core';

import { DIALOG_DATA } from '@treetop/shared/model';

import { DialogConfig } from './dialog-config';
import { DialogRef } from './dialog-ref';
import { DialogComponent } from './dialog.component';

const defaultConfig: DialogConfig = {
  backdropClass: '',
  disableClose: false,
  panelClass: '',
  minWidth: 300,
  maxWidth: 700,
};

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  private overlay = inject(Overlay);

  open<T, D = any>(componentOrTemplate: ComponentType<any> | TemplateRef<any>, config: Partial<DialogConfig<T>> = {}): DialogRef<D> {
    const dialogConfig: DialogConfig = { ...defaultConfig, ...config };

    const overlayRef = this.overlay.create({
      hasBackdrop: true,
      positionStrategy: this.overlay.position().global().centerVertically().centerHorizontally(),
      minWidth: config.minWidth ? config.minWidth : defaultConfig.minWidth,
      maxWidth: config.maxWidth ? config.maxWidth : defaultConfig.maxWidth,
      width: config.width ? config.width : defaultConfig.width,
      panelClass: config.panelClass ? config.panelClass : defaultConfig.panelClass,
      disposeOnNavigation: true,
    });

    const dialogRef = new DialogRef(overlayRef, dialogConfig);

    const dialog = overlayRef.attach(
      new ComponentPortal(
        DialogComponent,
        null,
        Injector.create({ providers: [{ provide: DialogRef, useValue: dialogRef }, ...(config.providers ?? [])] }),
      ),
    ).instance;

    dialog.data = config;

    if (componentOrTemplate instanceof TemplateRef) {
      dialog.attachTemplatePortal(
        new TemplatePortal(componentOrTemplate, null, {
          $implicit: config.data,
          dialog: dialogRef,
        }),
      );
    } else {
      dialog.attachComponentPortal(
        new ComponentPortal(
          componentOrTemplate,
          null,
          Injector.create({
            providers: [
              { provide: DIALOG_DATA, useValue: config.data },
              { provide: DialogRef, useValue: dialogRef },
              ...(config.providers ?? []),
            ],
          }),
        ),
      );
    }

    return dialogRef;
  }
}
